"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TierPreview } from "@/components/tier-preview";
import { getAccessibilityLevel } from "@/lib/color-utils";
import { AccessibilityInfo } from "@/components/accessibility-info";
import { ColorPickerInput } from "@/components/color-picker-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ColorConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialLabelColor: string;
  initialBackgroundColor: string;
  onSave: (labelColor: string, backgroundColor: string) => void;
  tierLabel: string;
}

const isValidHexColor = (color: string): boolean => {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
};

export function ColorConfigModal({
  isOpen,
  onClose,
  initialLabelColor,
  initialBackgroundColor,
  onSave,
  tierLabel,
}: ColorConfigModalProps) {
  const [labelColor, setLabelColor] = useState(initialLabelColor);
  const [backgroundColor, setBackgroundColor] = useState(
    initialBackgroundColor
  );
  const [labelColorError, setLabelColorError] = useState("");
  const [backgroundColorError, setBackgroundColorError] = useState("");

  useEffect(() => {
    setLabelColor(initialLabelColor);
    setBackgroundColor(initialBackgroundColor);
    setLabelColorError("");
    setBackgroundColorError("");
  }, [initialLabelColor, initialBackgroundColor, isOpen]);

  if (!isOpen) return null;

  const handleLabelColorChange = (newColor: string) => {
    setLabelColor(newColor);
    if (newColor && !isValidHexColor(newColor)) {
      setLabelColorError("Must be a valid hex color (e.g., #FF0000)");
    } else {
      setLabelColorError("");
    }
  };

  const handleBackgroundColorChange = (newColor: string) => {
    setBackgroundColor(newColor);
    if (newColor && !isValidHexColor(newColor)) {
      setBackgroundColorError("Must be a valid hex color (e.g., #FFCCCC)");
    } else {
      setBackgroundColorError("");
    }
  };

  const handleSave = () => {
    // Validate both colors before saving
    let hasError = false;

    if (!isValidHexColor(labelColor)) {
      setLabelColorError("Must be a valid hex color (e.g., #FF0000)");
      hasError = true;
    }

    if (!isValidHexColor(backgroundColor)) {
      setBackgroundColorError("Must be a valid hex color (e.g., #FFCCCC)");
      hasError = true;
    }

    if (!hasError) {
      onSave(labelColor, backgroundColor);
      onClose();
    }
  };

  const handleCancel = () => {
    setLabelColor(initialLabelColor);
    setBackgroundColor(initialBackgroundColor);
    setLabelColorError("");
    setBackgroundColorError("");
    onClose();
  };

  const isValid =
    isValidHexColor(labelColor) && isValidHexColor(backgroundColor);

  // Calculate accessibility level
  const accessibilityInfo =
    isValid && getAccessibilityLevel(labelColor, backgroundColor);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto z-[60]"
        overlayClassName="z-[60]"
      >
        <DialogHeader>
          <DialogTitle>Configure Colors - Tier {tierLabel}</DialogTitle>
          <DialogDescription>
            Choose colors with good contrast for accessibility
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Preview */}
          <div className="flex flex-col items-center space-y-2">
            <Label>Preview</Label>
            <TierPreview
              labelColor={
                isValidHexColor(labelColor) ? labelColor : initialLabelColor
              }
              backgroundColor={
                isValidHexColor(backgroundColor)
                  ? backgroundColor
                  : initialBackgroundColor
              }
            />
          </div>

          {/* Accessibility Check */}
          <AccessibilityInfo accessibilityInfo={accessibilityInfo} />

          {/* Label Color */}
          <ColorPickerInput
            id="label-color"
            label="Label Color"
            color={labelColor}
            error={labelColorError}
            onColorChange={handleLabelColorChange}
          />

          {/* Background Color */}
          <ColorPickerInput
            id="background-color"
            label="Background Color"
            color={backgroundColor}
            error={backgroundColorError}
            onColorChange={handleBackgroundColorChange}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
